import { PortalDialogProps } from "@app/services/modal.service";
import Modal from "../modal/modal.component";
import { useEffect, useState } from "react";
import { ImageCloudHistory } from "@app/types";
import useObservable from "@core/hooks/use-observable.hook";
import { formatDate } from "@app/utils/util";
import ImageCloudHistoryService from "@app/services/http/image.cloud.history.service";
import { DEFAULT_PAGE, DEFAULT_TOTAL_PAGES } from "@app/constants";
import Pagination from "../pagination";
import { useT } from "@app/contexts/preferences.context";
import clsx from "clsx";
import { IcX } from "../icons";
import { StatusPill, iconBtn } from "../ui";

/** Lịch sử sử dụng Cloud: ImageCloudHistoryService.getImageCloudHistory như bản cũ */
function OrderCloudHistoryModal({ portalDialogRef, portalData }: PortalDialogProps) {
  const t = useT();
  const image_id = portalData?.image_id;
  const [cloudImageHistory, setCloudImageHistory] = useState<ImageCloudHistory[]>([]);

  const [page, setPage] = useState(DEFAULT_PAGE);
  const [totalPages, setTotalPages] = useState(DEFAULT_TOTAL_PAGES);

  const onPageChange = (page: number) => {
    setPage(page);
  };

  const { subscribeOnce } = useObservable();

  useEffect(() => {
    image_id &&
      subscribeOnce(ImageCloudHistoryService.getImageCloudHistory(image_id, page), (res) => {
        if (res.data.length) {
          setCloudImageHistory(res.data);
          setTotalPages(res.total_page);
        } else {
          setCloudImageHistory([]);
        }
      });
  }, [image_id, page]);

  const usage = (history: ImageCloudHistory) => {
    const diff = new Date(history.time_shutdown).getTime() - new Date(history.time_start).getTime();
    const hours = Math.floor(diff / 1000 / 60 / 60);
    const minutes = Math.floor((diff / 1000 / 60) % 60);

    return `${hours} ${t("giờ")} ${minutes.toString().padStart(2, "0")} ${t("phút")}`;
  };

  const th = "whitespace-nowrap px-4 py-2.5 font-semibold";

  return (
    <Modal onCancel={portalDialogRef.close} buttonCancelInChildren={false}>
      <div className="flex max-h-[min(86vh,720px)] w-[min(640px,calc(100vw-32px))] flex-col text-fg">
        <div className="flex items-start justify-between gap-3 border-b border-line px-6 pb-4 pt-5">
          <div className="min-w-0">
            <h2 className="text-[19px] font-bold">{t("Lịch sử sử dụng Cloud")}</h2>
            <p className="m-0 mt-0.5 truncate font-mono text-[12.5px] text-muted">{image_id}</p>
          </div>
          <button type="button" className={clsx(iconBtn, "!h-8 !w-8")} aria-label={t("Đóng")} onClick={() => portalDialogRef.close()}>
            <IcX className="h-4 w-4" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto px-6 py-4">
          {!cloudImageHistory.length ? (
            <p className="m-0 py-8 text-center text-muted">{t("Cloud chưa bật lần nào.")}</p>
          ) : (
            <table className="htbl w-full border-collapse text-left text-[13px] tabular-nums">
              <thead>
                <tr className="bg-surface-2 text-xs text-muted">
                  <th className={th}>{t("Bắt đầu")}</th>
                  <th className={th}>{t("Tắt máy lúc")}</th>
                  <th className={th}>{t("Sử dụng")}</th>
                </tr>
              </thead>
              <tbody>
                {cloudImageHistory.map((history) => (
                  <tr key={history._id} className="border-b border-line last:border-b-0">
                    <td data-label={t("Bắt đầu")} className="px-4 py-2.5">{formatDate(history.time_start)}</td>
                    <td data-label={t("Tắt máy lúc")} className="px-4 py-2.5">
                      {history.time_shutdown ? formatDate(history.time_shutdown) : <StatusPill tone="ok">{t("Đang chạy")}</StatusPill>}
                    </td>
                    <td data-label={t("Sử dụng")} className="px-4 py-2.5 font-semibold">{history.time_shutdown ? usage(history) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {totalPages > 1 && (
          <div className="flex justify-center border-t border-line px-6 py-3">
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={onPageChange} />
          </div>
        )}
      </div>
    </Modal>
  );
}

export default OrderCloudHistoryModal;
